import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  createDocument,
  type CreateDocumentRequest,
  type CreateDocumentResponse,
  getDocumentTypesQuery
} from "@/api";
import {
  EditDocumentOrNote,
  type EditDocumentOrNoteFormSubmitValues
} from "@/components/EditDocumentOrNote";
import { showError, showSuccess } from "@/notifications/Notifications";
import { canAddOrEditProspectDocuments } from "@/security/logic";

export const Route = createFileRoute("/prospects/$prospectId/documents/new")({
  component: NewDocumentComponent,
  beforeLoad: ({ context, location }) => {
    const { userClaim, prospect } = context;

    // redirect from /new if user lacks edit permissions
    if (
      prospect &&
      !canAddOrEditProspectDocuments(userClaim, prospect.communityId)
    ) {
      throw redirect({
        to: "/notAuthorized/$",
        params: { _splat: location.href }
      });
    }
  }
});

function NewDocumentComponent() {
  const { prospectId } = Route.useParams();
  const navigate = useNavigate();
  const { secureAxios } = Route.useRouteContext().apiHook;
  const queryClient = useQueryClient();

  const { data: documentTypes = [] } = useQuery(
    getDocumentTypesQuery(secureAxios)
  );

  const handleClose = useCallback(() => {
    void navigate({
      to: "/prospects/$prospectId/documents",
      params: { prospectId }
    });
  }, [navigate, prospectId]);

  const createDocumentMutation = useMutation<
    CreateDocumentResponse,
    Error,
    CreateDocumentRequest
  >({
    mutationFn: async (request) => {
      return await createDocument(secureAxios, request);
    },
    onSuccess: () => {
      showSuccess("The document has been successfully created.");
      // Invalidate the documents list query to refresh the list
      void queryClient.invalidateQueries({
        queryKey: ["documents", "prospect", Number(prospectId)]
      });
      handleClose();
    },
    onError: (error) => {
      showError(
        error instanceof Error ? error.message : "An unknown error occurred",
        "Error creating document"
      );
    }
  });

  const handleSubmit = useCallback(
    (values: EditDocumentOrNoteFormSubmitValues) => {
      createDocumentMutation.mutate({
        prospectId: Number(prospectId),
        title: values.title,
        typeId: values.documentTypeId,
        includeDetailsInLivingStory: values.includeInLivingStory,
        detailsHtml: values.detailsHtml,
        file: values.file
      });
    },
    [createDocumentMutation, prospectId]
  );

  return (
    <EditDocumentOrNote
      documentTypes={documentTypes}
      isSubmitting={createDocumentMutation.isPending}
      onSubmit={handleSubmit}
      onClose={handleClose}
      title="Create Document or Note"
      submitButtonText="Create"
    />
  );
}
